import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { UiPieChartLabelMode, UiPieChartSlice, UiPieChartVariant } from './pie-chart.types';

/** @internal Segmento calculado para renderizar el SVG — no forma parte de la API pública. */
interface PieSegment {
  slice: UiPieChartSlice;
  path: string;
  color: string;
  pct: number;
  pctLabel: string;
  midAngleRad: number;
  hoverDx: number;
  hoverDy: number;
  labelX: number;
  labelY: number;
  insideX: number;
  insideY: number;
  animDelay: string;
}

const DEFAULT_PALETTE = [
  '#4169e2', '#7b96f5', '#f97316', '#22c55e',
  '#eab308', '#ef4444', '#8b5cf6', '#06b6d4',
];

/**
 * Organismo: gráfico de pastel presentacional.
 *
 * Implementado con SVG nativo — sin dependencias de charting externas.
 * Soporta 4 variantes: pie · donut · semi (gauge) · monochromatic.
 *
 * @example
 * <ui-pie-chart
 *   [slices]="mySlices"
 *   variant="donut"
 *   centerValue="68%"
 *   centerLabel="Completado"
 *   (sliceClick)="onSlice($event)"
 * />
 */
@Component({
  selector: 'ui-pie-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss',
})
export class UiPieChartComponent {
  // ── Inputs ──────────────────────────────────────────────────────────────
  /** Sectores del gráfico con id, label y value */
  readonly slices = input<UiPieChartSlice[]>([]);
  /** Forma visual del gráfico */
  readonly variant = input<UiPieChartVariant>('pie');
  /** Tamaño del canvas SVG en píxeles (ancho y alto base) */
  readonly size = input<number>(280);
  /** Grosor del anillo como fracción del radio (0–1); aplica a `donut` y `semi` */
  readonly donutThickness = input<number>(0.4);
  /** Título visible sobre el gráfico */
  readonly title = input<string>('');
  /** Valor grande en el centro del donut / semi (ej: "68%") */
  readonly centerValue = input<string>('');
  /** Etiqueta pequeña bajo el valor central (ej: "Completado") */
  readonly centerLabel = input<string>('');
  /** Modo de visualización de etiquetas sobre el SVG */
  readonly labelMode = input<UiPieChartLabelMode>('tooltip-only');
  /** Color base para generar la paleta monocromática (hex) */
  readonly monochromaticColor = input<string>('#4169e2');
  /** Paleta de colores custom; sobreescribe la paleta por defecto */
  readonly palette = input<string[]>([]);
  /** Mostrar leyenda interactiva debajo del gráfico */
  readonly showLegend = input<boolean>(true);
  /** Dibuja un círculo gris cuando la suma de los sectores es cero. */
  readonly showEmptyPlaceholder = input(false);
  /** Estado de carga — muestra skeleton en lugar del gráfico */
  readonly loading = input<boolean>(false);
  /** Activar animación de entrada por segmento */
  readonly animated = input<boolean>(true);

  // ── Outputs ─────────────────────────────────────────────────────────────
  /** Emite el slice al hacer click sobre un sector o ítem de leyenda */
  readonly sliceClick = output<UiPieChartSlice>();
  /** Emite el slice en hover; `null` cuando el cursor sale */
  readonly sliceHover = output<UiPieChartSlice | null>();

  // ── Estado UI interno ────────────────────────────────────────────────────
  readonly hoveredId = signal<string | null>(null);
  readonly activeId = signal<string | null>(null);
  readonly tooltipVisible = signal(false);
  readonly tooltipX = signal(0);
  readonly tooltipY = signal(0);
  readonly tooltipSeg = signal<PieSegment | null>(null);

  // ── Geometría derivada ───────────────────────────────────────────────────
  readonly cx = computed(() => this.size() / 2);
  readonly cy = computed(() => this.size() / 2);

  readonly outerR = computed(() => {
    const s = this.size();
    return this.labelMode() === 'outside' ? s * 0.35 : s * 0.43;
  });

  readonly innerR = computed(() => {
    const v = this.variant();
    return v === 'donut' || v === 'semi'
      ? this.outerR() * (1 - this.donutThickness())
      : 0;
  });

  /** Tamaño del valor central: 22px si cabe; si no, se reduce al hueco. */
  readonly centerValueFontSize = computed(() => {
    const text = this.centerValue();
    const base = 22;
    if (!text) return base;

    const hole = this.innerR() > 0 ? this.innerR() * 2 * 0.86 : this.size() * 0.42;
    const widthAtBase = text.length * base * 0.58;
    if (widthAtBase <= hole) return base;
    return Math.max(10, Math.floor(base * (hole / widthAtBase)));
  });

  /**
   * Recorte del semi al arco y al texto bajo la base.
   * El radio no llega al borde del canvas y la etiqueta cuelga debajo de `cy`.
   */
  private readonly semiBounds = computed(() => {
    const pad = 8;
    const top = Math.floor(this.cy() - this.outerR() - pad);
    const textBottom = this.centerLabel()
      ? this.cy() + (this.centerValue() ? 40 : 14) + 18
      : this.centerValue()
        ? this.cy() + 14 + 26
        : this.cy();
    const bottom = Math.ceil(Math.max(this.cy(), textBottom) + pad);

    return {
      x: -pad,
      y: top,
      width: this.size() + pad * 2,
      height: bottom - top,
    };
  });

  readonly svgViewBox = computed(() => {
    const s = this.size();
    const pad = 8;
    if (this.variant() === 'semi') {
      const frame = this.semiBounds();
      return `${frame.x} ${frame.y} ${frame.width} ${frame.height}`;
    }
    const ext = this.labelMode() === 'outside' ? 30 : 0;
    return `${-ext} ${-ext} ${s + ext * 2} ${s + ext * 2}`;
  });

  readonly svgHeight = computed(() => {
    const s = this.size();
    if (this.variant() === 'semi') return this.semiBounds().height;
    return this.labelMode() === 'outside' ? s + 60 : s;
  });

  readonly svgWidth = computed(() => {
    if (this.variant() === 'semi') return this.semiBounds().width;
    return this.labelMode() === 'outside' ? this.size() + 60 : this.size();
  });

  // ── Segmentos calculados ─────────────────────────────────────────────────
  protected readonly emptyPlaceholderVisible = computed(
    () => this.showEmptyPlaceholder() && this.segments().length === 0,
  );

  readonly segments = computed<PieSegment[]>(() => {
    const slices = this.slices();
    if (!slices.length) return [];
    const total = slices.reduce((s, sl) => s + sl.value, 0);
    if (total === 0) return [];

    const cx = this.cx();
    const cy = this.cy();
    const outerR = this.outerR();
    const innerR = this.innerR();
    const v = this.variant();
    const isSemi = v === 'semi';
    const isDonut = v === 'donut' || isSemi;
    const isMono = v === 'monochromatic';

    // Semi: el arco va de 270° (izquierda) a 90° (derecha) pasando por la cima
    const startAngle = isSemi ? 270 : 0;
    const totalDeg = isSemi ? 180 : 360;

    const pal = this.palette().length
      ? this.palette()
      : isMono
        ? this.monoShades(slices.length)
        : DEFAULT_PALETTE;

    let current = startAngle;
    return slices.map((slice, i) => {
      const pct = (slice.value / total) * 100;
      const deg = (slice.value / total) * totalDeg;
      const end = current + deg;
      const midDeg = current + deg / 2;
      const midRad = (midDeg - 90) * (Math.PI / 180);

      const color = slice.color ?? pal[i % pal.length];
      const hoverDist = outerR * 0.09;
      const labelDist = outerR + 20;
      const insideDist = isDonut ? (outerR + innerR) / 2 : outerR * 0.62;

      const seg: PieSegment = {
        slice,
        path: this.buildPath(cx, cy, outerR, innerR, current, end, isDonut),
        color,
        pct,
        pctLabel: `${pct.toFixed(1)}%`,
        midAngleRad: midRad,
        hoverDx: Math.cos(midRad) * hoverDist,
        hoverDy: Math.sin(midRad) * hoverDist,
        labelX: cx + Math.cos(midRad) * labelDist,
        labelY: cy + Math.sin(midRad) * labelDist,
        insideX: cx + Math.cos(midRad) * insideDist,
        insideY: cy + Math.sin(midRad) * insideDist,
        animDelay: `${i * 75}ms`,
      };
      current = end;
      return seg;
    });
  });

  // ── Event handlers ───────────────────────────────────────────────────────
  onSegmentEnter(seg: PieSegment): void {
    this.hoveredId.set(seg.slice.id);
    this.tooltipSeg.set(seg);
    this.tooltipVisible.set(true);
    this.sliceHover.emit(seg.slice);
  }

  onSegmentLeave(): void {
    this.hoveredId.set(null);
    this.tooltipVisible.set(false);
    this.sliceHover.emit(null);
  }

  onSegmentClick(seg: PieSegment): void {
    const next = this.activeId() === seg.slice.id ? null : seg.slice.id;
    this.activeId.set(next);
    this.sliceClick.emit(seg.slice);
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.tooltipVisible()) return;
    const wrap = event.currentTarget as HTMLElement;
    const rect = wrap.getBoundingClientRect();
    const cursorX = event.clientX - rect.left;
    const tooltipWidth =
      wrap.querySelector<HTMLElement>('.ui-pie-chart__tooltip')?.offsetWidth ??
      0;
    const rightX = cursorX + 16;
    const leftX = cursorX - tooltipWidth - 16;
    const maxX = Math.max(0, rect.width - tooltipWidth);
    const nextX =
      tooltipWidth > 0 && rightX + tooltipWidth > rect.width
        ? leftX
        : rightX;
    this.tooltipX.set(Math.min(Math.max(0, nextX), maxX));
    this.tooltipY.set(event.clientY - rect.top - 56);
  }

  onLegendEnter(seg: PieSegment): void {
    this.hoveredId.set(seg.slice.id);
    this.sliceHover.emit(seg.slice);
  }

  onLegendLeave(): void {
    this.hoveredId.set(null);
    this.sliceHover.emit(null);
  }

  // ── Template helpers ─────────────────────────────────────────────────────
  protected legendValue(slice: UiPieChartSlice): string {
    return slice.valueLabel ?? slice.value.toLocaleString('es-MX');
  }

  protected textAnchor(seg: PieSegment): string {
    const dx = seg.labelX - this.cx();
    if (Math.abs(dx) < 10) return 'middle';
    return dx > 0 ? 'start' : 'end';
  }

  protected segTransform(seg: PieSegment): string {
    const isActive = this.activeId() === seg.slice.id;
    const isHovered = this.hoveredId() === seg.slice.id;
    return isActive || isHovered
      ? `translate(${seg.hoverDx}px, ${seg.hoverDy}px)`
      : 'none';
  }

  protected segOpacity(seg: PieSegment): number {
    const hovered = this.hoveredId();
    if (!hovered) return 1;
    return hovered === seg.slice.id ? 1 : 0.52;
  }

  // ── SVG path building ────────────────────────────────────────────────────
  private polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
    const rad = (angleDeg - 90) * (Math.PI / 180);
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  private n(v: number): string {
    return v.toFixed(2);
  }

  private buildPath(
    cx: number,
    cy: number,
    outerR: number,
    innerR: number,
    startAngle: number,
    endAngle: number,
    isDonut: boolean,
  ): string {
    const delta = endAngle - startAngle;
    if (delta >= 359.999) {
      return this.fullCirclePath(cx, cy, outerR, innerR, isDonut);
    }
    const large = delta > 180 ? 1 : 0;
    const os = this.polarToCartesian(cx, cy, outerR, startAngle);
    const oe = this.polarToCartesian(cx, cy, outerR, endAngle);

    if (isDonut) {
      const innerStart = this.polarToCartesian(cx, cy, innerR, startAngle);
      const innerEnd = this.polarToCartesian(cx, cy, innerR, endAngle);
      return [
        `M ${this.n(os.x)} ${this.n(os.y)}`,
        `A ${this.n(outerR)} ${this.n(outerR)} 0 ${large} 1 ${this.n(oe.x)} ${this.n(oe.y)}`,
        `L ${this.n(innerEnd.x)} ${this.n(innerEnd.y)}`,
        `A ${this.n(innerR)} ${this.n(innerR)} 0 ${large} 0 ${this.n(innerStart.x)} ${this.n(innerStart.y)}`,
        'Z',
      ].join(' ');
    }
    return [
      `M ${this.n(cx)} ${this.n(cy)}`,
      `L ${this.n(os.x)} ${this.n(os.y)}`,
      `A ${this.n(outerR)} ${this.n(outerR)} 0 ${large} 1 ${this.n(oe.x)} ${this.n(oe.y)}`,
      'Z',
    ].join(' ');
  }

  private fullCirclePath(
    cx: number,
    cy: number,
    outerR: number,
    innerR: number,
    isDonut: boolean,
  ): string {
    const top = { x: cx, y: cy - outerR };
    const bot = { x: cx, y: cy + outerR };
    if (isDonut) {
      const it = { x: cx, y: cy - innerR };
      const ib = { x: cx, y: cy + innerR };
      return [
        `M ${this.n(top.x)} ${this.n(top.y)}`,
        `A ${this.n(outerR)} ${this.n(outerR)} 0 1 1 ${this.n(bot.x)} ${this.n(bot.y)}`,
        `A ${this.n(outerR)} ${this.n(outerR)} 0 1 1 ${this.n(top.x)} ${this.n(top.y)} Z`,
        `M ${this.n(it.x)} ${this.n(it.y)}`,
        `A ${this.n(innerR)} ${this.n(innerR)} 0 1 0 ${this.n(ib.x)} ${this.n(ib.y)}`,
        `A ${this.n(innerR)} ${this.n(innerR)} 0 1 0 ${this.n(it.x)} ${this.n(it.y)} Z`,
      ].join(' ');
    }
    return [
      `M ${this.n(top.x)} ${this.n(top.y)}`,
      `A ${this.n(outerR)} ${this.n(outerR)} 0 1 1 ${this.n(bot.x)} ${this.n(bot.y)}`,
      `A ${this.n(outerR)} ${this.n(outerR)} 0 1 1 ${this.n(top.x)} ${this.n(top.y)} Z`,
    ].join(' ');
  }

  // ── Color helpers ────────────────────────────────────────────────────────
  private monoShades(count: number): string[] {
    const rgb = this.hexToRgb(this.monochromaticColor());
    const { h, s } = this.rgbToHsl(rgb.r, rgb.g, rgb.b);
    return Array.from({ length: count }, (_, i) => {
      const t = count <= 1 ? 0.5 : i / (count - 1);
      const l = 28 + t * 54;
      return `hsl(${h}, ${Math.round(s * 0.82)}%, ${l}%)`;
    });
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    const c = hex.replace('#', '');
    const full = c.length === 3 ? c.split('').map(x => x + x).join('') : c;
    const num = parseInt(full, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  }

  private rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        default: h = ((r - g) / d + 4) / 6;
      }
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  }
}
